import {
  reactive,
  ref,
  Ref,
  UnwrapRef,
  watch,
  computed,
  ComponentInternalInstance,
  getCurrentInstance,
  ComputedRef,
} from '@vue/composition-api';
import _, { reject } from 'lodash';

import i18n, { i18n_t } from '@/locales/i18n';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips, { MsgError } from '@/renderer/scripts/utils/tips';
import { dispatchRouter } from '@/renderer/scripts/utils/tools/dispatchRouter';
import { httpUrl } from '@/renderer/scripts/http';
import {
  AccountProtocolParams,
  CasProtocol,
  OauthProtocol,
  OidcProtocol,
  OpenLdapProtocol,
  OpenLdapProtocolFormData,
  SupwisdomProtocolParams,
  ProtocolParams,
} from '@/renderer/scripts/libs/smh-sdk-class/typings';
import errorCodeMap from '@/renderer/scripts/utils/errorCode/index';

type WeworkConfigParams = SMH.WeworkConfigParams & {
  [key: string]: string | boolean | number | undefined;
};
interface Handler {
  showWeworkSelector: Ref<boolean>;
  showWework: Ref<boolean>;
  showWeworkDelegate: Ref<boolean>;
  showWeworkDelegateConfigInfo: Ref<boolean>;
  showOrganization: Ref<boolean>;
  loadingCode: Ref<boolean>;
  qrCodeUrl: Ref<string>;
  showSSO: Ref<boolean>;
  disableSave: Ref<boolean>;
  disableOperate: UnwrapRef<any>;
  noneAuthActive: ComputedRef<boolean>;
  anyLoginActive: ComputedRef<boolean>;
  anySyncActive: ComputedRef<boolean>;
  originData: UnwrapRef<any>;
  modifyData: UnwrapRef<any>;
  configStatusTips: UnwrapRef<any>;
  delegateOrgInfo: {
    name: string;
    roundLogo: string;
    squareLogo: string;
  };
  methods: {
    getWeworkConfig: () => Promise<void>;
    getIDaasConfig: () => Promise<void>;
    handleSave: () => Promise<void>;
    modfiyWeworkConfig: (data: WeworkConfigParams, isInit?: boolean) => void;
    modfiyIDaasConfig: (data: SMH.IDaasConfigParams, isInit?: boolean) => void;
    getAccountProtocol: () => Promise<void>;
    modfiyAccountProtocol: (data: AccountProtocolParams[], autoOrgSyncEnabled: boolean) => void;
    testAccountProtocol: (config: AccountProtocolParams) => Promise<{ testResult: string; testDetail?: any }>;
    updateAccountProtocol: (config: AccountProtocolParams) => void;
    weworkTypeChoosed: (type: 'self' | 'delegate') => void;
    getWeworkDelegateQrcode: () => Promise<void>;
    closeWeworkConfig: () => Promise<void>;
    closeYufuConfig: (ssoType: 'sso' | 'sync' | string) => Promise<void>;
    removeAccountProtocol: (protocol: ProtocolParams) => Promise<void>;
    resetLoginWayAndSyncWay: (removeLoginWay: string, removeSyncWay?: string) => Promise<void>;
    weworkAuthorized: () => Promise<void>;
    weworkOnline: () => Promise<void>;
    getDelegateInfo: () => Promise<void>;
    openWecomConsolePage: () => void;
    openManual: () => void;
  };
}

export default function handler(): Handler {
  const currComponent: ComponentInternalInstance = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;
  const store = vm.$store;
  const showWeworkSelector = ref(false);
  const showWework = ref(false);
  const showWeworkDelegate = ref(false);
  const showWeworkDelegateConfigInfo = ref(false);
  const showOrganization = ref(false);
  const showSSO = ref(false);
  const qrCodeUrl = ref('');
  // 对应checkbox/radio的禁用状态
  const disableOperate = reactive<any>({
    weworkLogin: true,
    casLogin: true,
    ldapLogin: true,
    windowsAdLogin: true,
    oauthLogin: true,
    oidcLogin: true,
    IDaasSync: true,
    SSOLogin: true,
    supwisdomSync: true,
  });

  // 认证关联状态
  const noneAuthActive = computed(() => Object.keys(disableOperate).every(key => disableOperate[key] === true));

  // 是否关联任意一种登录方式
  const anyLoginActive = computed(
    () =>
      Object.keys(disableOperate).filter(
        key => disableOperate[key] === false && !['IDaasSync', 'supwisdomSync'].includes(key),
      ).length !== 0,
  );

  // 是否关联了任意一种架构同步方式
  const anySyncActive = computed(
    () =>
      Object.keys(disableOperate).filter(
        key => disableOperate[key] === false && !['SSOLogin', 'oauthLogin', 'oidcLogin', 'casLogin'].includes(key),
      ).length !== 0,
  );

  const ssoWayOptionsValue = computed(() => store.getters.ssoWay || (store.getters.enableYufuLogin ? 'yufu' : ''));

  const defaultWeworkConfig = {
    corpid: '',
    agentId: 0,
    secret: '',
    autoOrgSyncEnabled: false,
    type: '',
    status: '',
  };

  const defaultIdaasConfig = {
    tenantName: '',
    autoOrgSyncEnabled: false,
    clientId: '',
    clientSecret: '',
    wellKnown: '',
    oauthUrl: '',
    callbackUrls: [],
    serviceAccount: {},
  };

  const defaultLdapConfig = {
    type: 'openLdap',
    protocol: 'ldap',
    hostname: '',
    port: '389',
    bindDN: '',
    bindPassword: '',
    searchDN: '',
    departmentObjectclass: 'organizationalUnit',
    userObjectclass: 'inetOrgPerson',
    loginFlag: 'uid',
    loginFlagShow: '',
  };

  const defaultWindowsAdConfig = {
    type: 'windowsAd',
    protocol: 'ldap',
    hostname: '',
    port: '389',
    bindDN: '',
    bindPassword: '',
    searchDN: '',
    departmentObjectclass: 'organizationalUnit',
    userObjectclass: 'user',
    loginFlag: 'sAMAccountName',
    loginFlagShow: '',
  };

  const defaultCasConfig = {
    redirectUri: '',
    loginFlag: 'user',
    ssoEndpoint: '',
    userInfoEndpoint: '',
    isCreateAccountOnLogin: false,
  };

  const defaultOauthConfig = {
    redirectUri: '',
    clientId: '',
    clientSecret: '',
    ssoEndpoint: '',
    tokenEndpoint: '',
    userInfoEndpoint: '',
    loginFlag: '',
    scopeRange: [],
    isCreateAccountOnLogin: false,
  };

  const defaultOIDCConfig = {
    redirectUri: '',
    clientId: '',
    clientSecret: '',
    ssoEndpoint: '',
    tokenEndpoint: '',
    loginFlag: '',
    jwksUri: '',
    jwtSecret: '',
    isCreateAccountOnLogin: false,
  };

  const defaultSupwisdomConfig = {
    appId: '',
    appSecret: '',
    dataEndpoint: '',
    tokenEndpoint: '',
    userInterFaceId: '',
    departmentInterfaceId: '',
    availableDepartmentId: '',
    availableStatus: '',
    isHideAvailableDepartmentId: false,
  };

  /*
    enableWeworkLogin ,enableYufuLogin, syncWay
    分别为对应checkbox/radio的选中状态
  */
  // 原始数据
  const originData = reactive<{
    enableWeworkLogin: boolean;
    enableYufuLogin: boolean;
    enableOpenLdapLogin: boolean;
    enableWindowsAdLogin: boolean;
    autoOrgSyncEnabled: boolean;
    ssoWay: string;
    syncWay: string;
    weworkConfig: WeworkConfigParams;
    idaasConfig: SMH.IDaasConfigParams;
    ldapConfig: OpenLdapProtocolFormData;
    supwisdomConfig: SupwisdomProtocolParams;
    windowsAdConfig: OpenLdapProtocolFormData;
    casConfig: CasProtocol;
    oauthConfig: OidcProtocol;
    oidcConfig: OidcProtocol;
  }>({
    enableWeworkLogin: store.getters.enableWeworkLogin,
    enableYufuLogin: store.getters.enableYufuLogin,
    enableOpenLdapLogin: store.getters.enableOpenLdapLogin,
    enableWindowsAdLogin: store.getters.enableWindowsAdLogin,
    autoOrgSyncEnabled: false,
    ssoWay: ssoWayOptionsValue.value,
    syncWay: store.getters.syncWay,
    weworkConfig: _.cloneDeep(defaultWeworkConfig),
    idaasConfig: _.cloneDeep(defaultIdaasConfig),
    ldapConfig: _.cloneDeep(defaultLdapConfig),
    supwisdomConfig: _.cloneDeep(defaultSupwisdomConfig),
    windowsAdConfig: _.cloneDeep(defaultWindowsAdConfig),
    casConfig: _.cloneDeep(defaultCasConfig),
    oauthConfig: _.cloneDeep(defaultOauthConfig),
    oidcConfig: _.cloneDeep(defaultOIDCConfig),
  });

  // 更新数据
  const modifyData = reactive({
    enableWeworkLogin: store.getters.enableWeworkLogin,
    enableYufuLogin: store.getters.enableYufuLogin,
    enableOpenLdapLogin: store.getters.enableOpenLdapLogin,
    enableWindowsAdLogin: store.getters.enableWindowsAdLogin,
    autoOrgSyncEnabled: false,
    ssoWay: ssoWayOptionsValue.value,
    syncWay: store.getters.syncWay,
    weworkConfig: _.cloneDeep(defaultWeworkConfig),
    idaasConfig: _.cloneDeep(defaultIdaasConfig),
    ldapConfig: _.cloneDeep(defaultLdapConfig),
    windowsAdConfig: _.cloneDeep(defaultWindowsAdConfig),
    casConfig: _.cloneDeep(defaultCasConfig),
    oauthConfig: _.cloneDeep(defaultOauthConfig),
    oidcConfig: _.cloneDeep(defaultOIDCConfig),
    supwisdomConfig: _.cloneDeep(defaultSupwisdomConfig),
  });

  const configStatusTips = reactive({
    weworkValidate: {
      code: 'none',
      message: i18n_t('未配置'),
    },
    casValidate: {
      code: 'none',
      message: i18n_t('未配置'),
    },
    ldapValidate: {
      code: 'none',
      message: i18n_t('未配置'),
    },
    windowsAdValidate: {
      code: 'none',
      message: i18n_t('未配置'),
    },
    oidcValidate: {
      code: 'none',
      message: i18n_t('未配置'),
    },
    oauthValidate: {
      code: 'none',
      message: i18n_t('未配置'),
    },
    idaasValidate: {
      code: 'none',
      message: i18n_t('未配置'),
    },
    ssoValidate: {
      code: 'none',
      message: i18n_t('未配置'),
    },
    supwisdomValidate: {
      code: 'none',
      message: i18n_t('未配置'),
    },
  });

  const delegateOrgInfo = reactive({
    name: '',
    squareLogo: '',
    roundLogo: '',
  });

  // 禁用保存按钮
  const disableSave = computed(() => _.isEqual(modifyData, originData));
  const loadingCode = ref(false);

  const methods = {
    initAccountProtocolConfig() {
      modifyData.casConfig = _.cloneDeep(defaultCasConfig);
      originData.casConfig = _.cloneDeep(defaultCasConfig);
      modifyData.ldapConfig = _.cloneDeep(defaultLdapConfig);
      originData.ldapConfig = _.cloneDeep(defaultLdapConfig);
      modifyData.windowsAdConfig = _.cloneDeep(defaultWindowsAdConfig);
      originData.windowsAdConfig = _.cloneDeep(defaultWindowsAdConfig);
      modifyData.supwisdomConfig = _.cloneDeep(defaultSupwisdomConfig);
      originData.supwisdomConfig = _.cloneDeep(defaultSupwisdomConfig);
      modifyData.oauthConfig = _.cloneDeep(defaultOauthConfig);
      originData.oauthConfig = _.cloneDeep(defaultOauthConfig);
      originData.oidcConfig = _.cloneDeep(defaultOIDCConfig);
      modifyData.oidcConfig = _.cloneDeep(defaultOIDCConfig);

      disableOperate.casLogin = true;
      disableOperate.ldapLogin = true;
      disableOperate.windowsAdLogin = true;
      disableOperate.oauthLogin = true;
      disableOperate.oidcLogin = true;
      disableOperate.supwisdomSync = true;
      configStatusTips.casValidate.code = 'none';
      configStatusTips.oauthValidate.code = 'none';
      configStatusTips.oidcValidate.code = 'none';
      configStatusTips.ldapValidate.code = 'none';
      configStatusTips.windowsAdValidate.code = 'none';
    },
    async getWeworkConfig() {
      try {
        const { data } = await smh.userAPI.getWeworkConfig();
        if (!data.corpid || !data.agentId || !data.secret) {
          Object.assign(originData.weworkConfig, { type: '', status: '' });
        }
        Object.assign(originData.weworkConfig, data);
        methods.modfiyWeworkConfig(data as WeworkConfigParams);
      } catch (error) {
        tips.error(error as MsgError);
      }
    },
    async updateWeworkSyncConfig(autoOrgSyncEnabled?: boolean) {
      try {
        const params = modifyData.weworkConfig.type === 'self' ? modifyData.weworkConfig : {};
        await smh.userAPI.updateWeworkConfig({
          ...params,
          autoOrgSyncEnabled: _.isNil(autoOrgSyncEnabled)
            ? modifyData.weworkConfig.autoOrgSyncEnabled
            : autoOrgSyncEnabled,
        });
        modifyData.weworkConfig.autoOrgSyncEnabled = _.isNil(autoOrgSyncEnabled)
          ? modifyData.weworkConfig.autoOrgSyncEnabled
          : autoOrgSyncEnabled;
      } catch (error) {
        tips.error(error as MsgError);
      }
    },
    modfiyWeworkConfig(data: WeworkConfigParams) {
      Object.assign(modifyData.weworkConfig, data);
      if (data.type === 'self') {
        configStatusTips.weworkValidate.code = data.status === 'active' ? 'success' : 'none';
        disableOperate.weworkLogin = data.status !== 'active';
      } else if (data.type === 'delegate') {
        const status = (data.status as string) || 'none';
        configStatusTips.weworkValidate.code = status === 'active' ? 'success' : status;
        disableOperate.weworkLogin = data.status !== 'active';
      } else if (
        !modifyData.weworkConfig.corpid ||
        !modifyData.weworkConfig.agentId ||
        !modifyData.weworkConfig.secret
      ) {
        disableOperate.weworkLogin = true;
        configStatusTips.weworkValidate.code = 'none';
        Object.assign(modifyData.weworkConfig, { type: '', status: '' });
      }
    },
    async getIDaasConfig() {
      try {
        const { data } = await smh.userAPI.getIDaasConfig();
        Object.assign(originData.idaasConfig, data);
        methods.modfiyIDaasConfig(data as SMH.IDaasConfigParams);
      } catch (error) {
        tips.error(error as MsgError);
      }
    },
    async updateIDaasSyncConfig(autoOrgSyncEnabled?: boolean) {
      try {
        await smh.userAPI.updateIDaasConfig({
          type: 'sync',
          tenantName: modifyData.idaasConfig.tenantName,
          serviceAccount: modifyData.idaasConfig.serviceAccount,
          autoOrgSyncEnabled: _.isNil(autoOrgSyncEnabled)
            ? modifyData.idaasConfig.autoOrgSyncEnabled
            : autoOrgSyncEnabled,
        });
        modifyData.idaasConfig.autoOrgSyncEnabled = _.isNil(autoOrgSyncEnabled)
          ? modifyData.idaasConfig.autoOrgSyncEnabled
          : autoOrgSyncEnabled;
      } catch (error) {
        tips.error(error as MsgError);
      }
    },
    async updateLdapSyncConfig(autoOrgSyncEnabled?: boolean) {
      try {
        const ldapUrl = `${modifyData.ldapConfig.protocol}://${modifyData.ldapConfig.hostname}:${modifyData.ldapConfig.port}`;
        const adUrl = `${modifyData.windowsAdConfig.protocol}://${modifyData.windowsAdConfig.hostname}:${modifyData.windowsAdConfig.port}`;
        if (modifyData.syncWay === 'openLdap') {
          await methods.updateAccountProtocol({
            protocol: 'openLdap',
            config: { url: ldapUrl, ...modifyData.ldapConfig },
            autoOrgSyncEnabled: _.isNil(autoOrgSyncEnabled) ? modifyData.autoOrgSyncEnabled : autoOrgSyncEnabled,
          });
          modifyData.autoOrgSyncEnabled = _.isNil(autoOrgSyncEnabled)
            ? modifyData.autoOrgSyncEnabled
            : autoOrgSyncEnabled;
        } else if (modifyData.syncWay === 'windowsAd') {
          await methods.updateAccountProtocol({
            protocol: 'windowsAd',
            config: { url: adUrl, ...modifyData.windowsAdConfig },
            autoOrgSyncEnabled: _.isNil(autoOrgSyncEnabled) ? modifyData.autoOrgSyncEnabled : autoOrgSyncEnabled,
          });
          modifyData.autoOrgSyncEnabled = _.isNil(autoOrgSyncEnabled)
            ? modifyData.autoOrgSyncEnabled
            : autoOrgSyncEnabled;
        } else if (modifyData.syncWay === 'supwisdom') {
          await methods.updateAccountProtocol({
            protocol: 'supwisdom',
            config: { ...modifyData.supwisdomConfig },
            autoOrgSyncEnabled: _.isNil(autoOrgSyncEnabled) ? modifyData.autoOrgSyncEnabled : autoOrgSyncEnabled,
          });
          modifyData.autoOrgSyncEnabled = _.isNil(autoOrgSyncEnabled)
            ? modifyData.autoOrgSyncEnabled
            : autoOrgSyncEnabled;
        } else {
          if (originData.syncWay === 'openLdap') {
            await methods.updateAccountProtocol({
              protocol: 'openLdap',
              config: { url: ldapUrl, ...modifyData.ldapConfig },
              autoOrgSyncEnabled: false,
            });
          } else if (originData.syncWay === 'windowsAd') {
            await methods.updateAccountProtocol({
              protocol: 'windowsAd',
              config: { url: adUrl, ...modifyData.windowsAdConfig },
              autoOrgSyncEnabled: false,
            });
          }
          modifyData.autoOrgSyncEnabled = false;
        }
      } catch (error) {
        tips.error(error as MsgError);
      }
    },
    modfiyIDaasConfig(data: SMH.IDaasConfigParams) {
      Object.assign(modifyData.idaasConfig, data);
      if (!modifyData.idaasConfig.tenantName || _.isEmpty(modifyData.idaasConfig.serviceAccount)) {
        disableOperate.IDaasSync = true;
        configStatusTips.idaasValidate.code = 'none';
      } else if (modifyData.idaasConfig.tenantName && !_.isEmpty(modifyData.idaasConfig.serviceAccount)) {
        disableOperate.IDaasSync = false;
        configStatusTips.idaasValidate.code = 'success';
      }

      if (
        !modifyData.idaasConfig.tenantName ||
        !modifyData.idaasConfig.clientId ||
        !modifyData.idaasConfig.clientSecret ||
        !modifyData.idaasConfig.clientSecret
      ) {
        disableOperate.SSOLogin = true;
        configStatusTips.ssoValidate.code = 'none';
      } else {
        disableOperate.SSOLogin = false;
        configStatusTips.ssoValidate.code = 'success';
      }
    },
    async getAccountProtocol() {
      try {
        methods.initAccountProtocolConfig();
        const { data } = await smh.userAPI.getAccountProtocol();
        const { autoOrgSyncEnabled } = data;
        originData.autoOrgSyncEnabled = autoOrgSyncEnabled;
        data.configList.forEach(item => {
          const { config } = item;
          switch (item.protocol) {
            case 'cas':
              Object.assign(originData.casConfig, config);
              break;
            case 'oauth':
              Object.assign(originData.oauthConfig, config);
              break;
            case 'oidc':
              Object.assign(originData.oidcConfig, config);
              break;
            case 'openLdap':
              {
                const { hostname, port, protocol } = methods.handleUrl(config);
                Object.assign(originData.ldapConfig, {
                  type: item.protocol,
                  ...config,
                  hostname,
                  port,
                  protocol,
                });
              }
              break;
            case 'windowsAd':
              {
                const { hostname, port, protocol } = methods.handleUrl(config);
                Object.assign(originData.windowsAdConfig, {
                  type: item.protocol,
                  ...config,
                  hostname,
                  port,
                  protocol,
                });
              }
              break;
            case 'supwisdom':
              Object.assign(originData.supwisdomConfig, config);
              break;
            default:
              break;
          }
        });
        methods.modfiyAccountProtocol(data.configList as AccountProtocolParams[], autoOrgSyncEnabled);
      } catch (error) {
        tips.error(error as MsgError);
      }
    },
    // 检查对象内某些key是否全部不为空 返回true则代表所以key都不为空
    checkKeysNotEmpty(obj: Record<string, any>, keyList: string[]) {
      const res = keyList.filter(key => _.isEmpty(obj[key]));
      return res.length === 0;
    },
    modfiyAccountProtocol(data: AccountProtocolParams[], autoOrgSyncEnabled: boolean) {
      modifyData.autoOrgSyncEnabled = autoOrgSyncEnabled;
      data.forEach(item => {
        const { config } = item as any;
        switch (item.protocol) {
          case 'cas':
            Object.assign(modifyData.casConfig, config);
            if (methods.checkKeysNotEmpty(config, ['redirectUri', 'ssoEndpoint', 'userInfoEndpoint'])) {
              disableOperate.casLogin = false;
              configStatusTips.casValidate.code = 'success';
            } else {
              disableOperate.casLogin = true;
              configStatusTips.casValidate.code = 'none';
            }
            break;
          case 'oauth':
            {
              Object.assign(modifyData.oauthConfig, config);
              if (
                methods.checkKeysNotEmpty(config, [
                  'redirectUri',
                  'clientId',
                  'clientSecret',
                  'ssoEndpoint',
                  'tokenEndpoint',
                  'userInfoEndpoint',
                  'loginFlag',
                ])
              ) {
                disableOperate.oauthLogin = false;
                configStatusTips.oauthValidate.code = 'success';
              } else {
                disableOperate.oauthLogin = true;
                configStatusTips.oauthValidate.code = 'none';
              }
            }
            break;
          case 'oidc':
            {
              Object.assign(modifyData.oidcConfig, config);
              if (
                methods.checkKeysNotEmpty(config, [
                  'redirectUri',
                  'clientId',
                  'clientSecret',
                  'ssoEndpoint',
                  'tokenEndpoint',
                  'loginFlag',
                ])
              ) {
                disableOperate.oidcLogin = false;
                configStatusTips.oidcValidate.code = 'success';
              } else {
                disableOperate.oidcLogin = true;
                configStatusTips.oidcValidate.code = 'none';
              }
            }
            break;
          case 'openLdap':
            {
              const { hostname, port, protocol } = methods.handleUrl(config);
              Object.assign(modifyData.ldapConfig, {
                type: item.protocol,
                ...config,
                hostname,
                port,
                protocol,
              });
              if (
                methods.checkKeysNotEmpty(config, [
                  'url',
                  'bindDN',
                  'bindPassword',
                  'searchDN',
                  'loginFlag',
                  'loginFlagShow',
                ])
              ) {
                disableOperate.ldapLogin = false;
                configStatusTips.ldapValidate.code = 'success';
              } else {
                disableOperate.ldapLogin = true;
                configStatusTips.ldapValidate.code = 'none';
              }
            }
            break;
          case 'windowsAd':
            {
              const { hostname, port, protocol } = methods.handleUrl(config);
              Object.assign(modifyData.windowsAdConfig, {
                type: item.protocol,
                ...config,
                hostname,
                port,
                protocol,
              });
              if (
                methods.checkKeysNotEmpty(config, [
                  'url',
                  'bindDN',
                  'bindPassword',
                  'searchDN',
                  'loginFlag',
                  'loginFlagShow',
                ])
              ) {
                disableOperate.windowsAdLogin = false;
                configStatusTips.windowsAdValidate.code = 'success';
              } else {
                disableOperate.windowsAdLogin = true;
                configStatusTips.windowsAdValidate.code = 'none';
              }
            }
            break;
          case 'supwisdom':
            Object.assign(modifyData.supwisdomConfig, config);
            if (
              methods.checkKeysNotEmpty(config, [
                'appId',
                'appSecret',
                'dataEndpoint',
                'tokenEndpoint',
                'userInterFaceId',
                'departmentInterfaceId',
              ])
            ) {
              disableOperate.supwisdomSync = false;
              configStatusTips.supwisdomValidate.code = 'success';
            } else {
              disableOperate.supwisdomSync = true;
              configStatusTips.supwisdomValidate.code = 'none';
            }
            break;
          default:
            break;
        }
      });
    },
    async testAccountProtocol(config: AccountProtocolParams) {
      return new Promise<{ testResult: string; testDetail?: any }>(async (resolve, reject) => {
        const { protocol } = config;
        try {
          if (['openLdap', 'windowsAd', 'supwisdom'].includes(protocol as string)) {
            const { data } = await smh.userAPI.testAccountProtocol(config);
            const { departments, users } = data as SMH.AccountTestResultData;
            const isTimeout = departments.status === 'timeout' || users.status === 'timeout';
            let showCount = true;
            if (isTimeout && departments.length > 0 && users.length > 0) {
              showCount = false;
            }
            resolve({ testResult: 'success', testDetail: { ...(data as SMH.AccountTestResultData), showCount } });
          } else {
            await smh.userAPI.testAccountProtocol(config);
            resolve({ testResult: 'success' });
          }
        } catch (err: any) {
          if (['openLdap', 'windowsAd', 'supwisdom'].includes(protocol as string)) {
            reject({
              testResult: 'fail',
              testDetail: {
                ...err,
                code: errorCodeMap[err?.code] || i18n_t('请检查内容后重新测试'),
              },
            });
          } else {
            reject({ testResult: 'fail' });
          }
        }
      });
    },
    async updateAccountProtocol(config: AccountProtocolParams) {
      try {
        await smh.userAPI.setAccountProtocol(config);
      } catch (error) {
        tips.error(error as MsgError);
      }
    },
    async removeAccountProtocol(protocol: ProtocolParams) {
      try {
        if (!protocol.protocol) return;
        const { ssoWay, syncWay, enableOpenLdapLogin, enableWindowsAdLogin } = originData;
        let resetSyncWay = '';
        switch (protocol.protocol) {
          case 'openLdap':
            if (ssoWay === 'openLdap' || syncWay === 'openLdap' || enableOpenLdapLogin) {
              resetSyncWay = 'openLdap';
            }
            break;
          case 'windowsAd':
            if (ssoWay === 'windowsAd' || syncWay === 'windowsAd' || enableWindowsAdLogin) {
              resetSyncWay = 'windowsAd';
            }
            break;
          case 'cas':
            if (ssoWay === 'cas') {
              resetSyncWay = 'cas';
            }
            break;
          case 'oauth':
            if (ssoWay === 'oauth') {
              resetSyncWay = 'oauth';
            }
            break;
          case 'oidc':
            if (ssoWay === 'oidc') {
              resetSyncWay = 'oidc';
            }
            break;
          case 'supwisdom':
            resetSyncWay = 'supwisdom';
            break;
          default:
            break;
        }
        if (resetSyncWay) {
          await methods.resetLoginWayAndSyncWay(resetSyncWay);
        }
        // 先清除同步和登录相关配置再解除关联
        await smh.userAPI.removeAccountProtocol(protocol);
        tips.success(i18n_t('解绑成功'));
        // 清除后更新企业上挂载的信息
        store.dispatch('GetOrgInfo', store.getters.orgId);
      } catch (error) {
        tips.error(error as MsgError);
      }
    },
    /**
     *  解除关联需要执行的业务逻辑：
     *    1.执行解除关联的api：预期之前的配置清空
     *    2.目前关联认证方式之后可以选中同步方式、自动同步、登录方式：因此解除关联之后需要将这些参数做处理
     */
    async resetLoginWayAndSyncWay(removeWay: string) {
      const { enableWeworkLogin, enableYufuLogin, enableOpenLdapLogin, enableWindowsAdLogin, ssoWay, syncWay } =
        originData;
      if (['openLdap', 'windowsAd', 'supwisdom'].includes(removeWay)) {
        // 处理登录方式和同步方式
        await smh.userAPI.updateOrgInfo({
          name: store.getters.orgName,
          extensionData: {
            enableWeworkLogin,
            enableYufuLogin: modifyData.ssoWay === 'yufu',
            enableOpenLdapLogin: removeWay === 'openLdap' ? false : enableOpenLdapLogin,
            enableWindowsAdLogin: removeWay === 'windowsAd' ? false : enableWindowsAdLogin,
            ssoWay: modifyData.ssoWay === 'yufu' ? '' : ssoWay,
            syncWay: syncWay === removeWay ? '' : syncWay,
          },
        });
        // 处理自动同步方式
        if (originData.syncWay === removeWay) {
          originData.autoOrgSyncEnabled && (await methods.updateLdapSyncConfig(false));
        }
      } else if (['oidc', 'oauth', 'cas'].includes(removeWay)) {
        await smh.userAPI.updateOrgInfo({
          name: store.getters.orgName,
          extensionData: {
            enableWeworkLogin,
            enableYufuLogin,
            enableOpenLdapLogin,
            enableWindowsAdLogin,
            ssoWay: ssoWay === removeWay ? '' : ssoWay,
            syncWay,
          },
        });
      }
    },
    async handleSave() {
      const {
        enableWeworkLogin,
        enableYufuLogin,
        enableOpenLdapLogin,
        enableWindowsAdLogin,
        ssoWay,
        syncWay,
        weworkConfig,
        idaasConfig,
        casConfig,
        ldapConfig,
        windowsAdConfig,
        supwisdomConfig,
      } = modifyData;
      // 修改架构同步方式，需要关闭其它架构同步方式
      if (syncWay === 'yufu') {
        await methods.updateIDaasSyncConfig();
        modifyData.weworkConfig.autoOrgSyncEnabled && (await methods.updateWeworkSyncConfig(false));
        modifyData.autoOrgSyncEnabled && (await methods.updateLdapSyncConfig(false));
      } else if (syncWay === 'wework') {
        await methods.updateWeworkSyncConfig();
        modifyData.idaasConfig.autoOrgSyncEnabled && (await methods.updateIDaasSyncConfig(false));
        modifyData.autoOrgSyncEnabled && (await methods.updateLdapSyncConfig(false));
      } else if (['openLdap', 'windowsAd'].includes(syncWay)) {
        await methods.updateLdapSyncConfig();
        modifyData.weworkConfig.autoOrgSyncEnabled && (await methods.updateWeworkSyncConfig(false));
        modifyData.idaasConfig.autoOrgSyncEnabled && (await methods.updateIDaasSyncConfig(false));
      } else if (syncWay === 'supwisdom') {
        await methods.updateLdapSyncConfig();
        modifyData.weworkConfig.autoOrgSyncEnabled && (await methods.updateWeworkSyncConfig(false));
        modifyData.idaasConfig.autoOrgSyncEnabled && (await methods.updateIDaasSyncConfig(false));
      }
      try {
        // 这里的ssoWay是作为下拉选择的一个值,需要进行转换;ssoWay如果是yufu的话需要设置enableYufuLogin为true,同时设置ssoWay为空字符串;
        await smh.userAPI.updateOrgInfo({
          name: store.getters.orgName,
          extensionData: {
            enableWeworkLogin,
            enableYufuLogin: modifyData.ssoWay === 'yufu',
            enableOpenLdapLogin,
            enableWindowsAdLogin,
            ssoWay: modifyData.ssoWay === 'yufu' ? '' : ssoWay,
            syncWay,
          },
        });
        tips.success(i18n_t('保存成功'));
        store.commit('SET_ORG_SSO_WAY', ssoWay);
        store.commit('SET_ORG_SYNC_WAY', syncWay);
        store.commit('SET_ENABLE_WEWORK', enableWeworkLogin);
        store.commit('SET_ENABLE_YUFU', enableYufuLogin);
        store.commit('SET_ENABLE_OPEN_LDAP', enableOpenLdapLogin);
        store.commit('SET_ENABLE_WINDOWS_AD', enableWindowsAdLogin);
        Object.assign(originData, modifyData);
        originData.weworkConfig = _.cloneDeep(weworkConfig);
        originData.idaasConfig = _.cloneDeep(idaasConfig);
        originData.ldapConfig = _.cloneDeep(ldapConfig);
        originData.casConfig = _.cloneDeep(casConfig);
        originData.oidcConfig = _.cloneDeep(originData.oidcConfig);
        originData.oauthConfig = _.cloneDeep(originData.oauthConfig);
        originData.windowsAdConfig = _.cloneDeep(windowsAdConfig);
        originData.supwisdomConfig = _.cloneDeep(supwisdomConfig);
      } catch (error) {
        tips.error(error as MsgError);
      }
    },
    handleUrl(config: any) {
      const { url } = config as OpenLdapProtocol;
      const protocol = url && url.split(':')[0];
      const hostname = url && url.split('//')[1].split(':')[0];
      const port = url && url.split(':')[2];
      return {
        protocol,
        hostname,
        port,
      };
    },
    // 清除企微配置
    async closeWeworkConfig() {
      try {
        // 如果解除的关联方式和选中的一样则需要更新对应的自动同步状态
        if (originData.syncWay === 'wework') {
          originData.weworkConfig.autoOrgSyncEnabled && (await methods.updateWeworkSyncConfig(false));
        }
        await smh.userAPI.removeWeworkConfig();
        tips.success(i18n_t('解绑成功'));
        Object.assign(originData.weworkConfig, {
          type: '',
          status: '',
        });
        Object.assign(modifyData.weworkConfig, {
          type: '',
          status: '',
        });
        // 清除后更新企业上挂载的信息
        store.dispatch('GetOrgInfo', store.getters.orgId);
      } catch (e: any) {
        tips.error(e);
      }
    },
    // 清除idaas配置
    async closeYufuConfig(type: 'sso' | 'sync' | string) {
      try {
        // 如果解除的关联方式和选中的一样则需要更新对应的自动同步状态
        if (originData.syncWay === 'yufu' && type === 'sync') {
          originData.idaasConfig.autoOrgSyncEnabled && (await methods.updateIDaasSyncConfig(false));
        }
        await smh.userAPI.removeYufuConfig({ type });
        tips.success(i18n_t('解绑成功'));
        // 清除后更新企业上挂载的信息
        store.dispatch('GetOrgInfo', store.getters.orgId);
      } catch (e: any) {
        tips.error(e);
      }
    },
    // 获取企微代开发的二维码
    async getWeworkDelegateQrcode() {
      try {
        loadingCode.value = true;
        const res = await smh.userAPI.getWeworkDelegateQrcode();
        qrCodeUrl.value = res.data.qrcodeUrl;
        loadingCode.value = false;
      } catch (e: any) {
        tips.error(e);
        qrCodeUrl.value = '';
        loadingCode.value = false;
      }
    },
    // 我已配置
    async weworkAuthorized() {
      try {
        await smh.userAPI.weworkAuthorized();
        tips.success(i18n_t('完成配置'));
      } catch (e: any) {
        tips.error(e);
      }
    },
    // 我已上线
    async weworkOnline() {
      try {
        await smh.userAPI.weworkOnline();
        tips.success(i18n_t('完成上线'));
      } catch (e: any) {
        tips.error(e);
      }
    },
    // 获取代开发应用信息
    async getDelegateInfo() {
      try {
        const res = await smh.userAPI.getDelegateInfo();
        const { name, squareLogo, roundLogo } = res.data;
        Object.assign(delegateOrgInfo, { name, squareLogo, roundLogo });
      } catch (e: any) {
        tips.error(e);
      }
    },
    weworkTypeChoosed(type: 'self' | 'delegate') {
      if (type === 'self') {
        showWework.value = true;
      } else if (type === 'delegate') {
        showWeworkDelegate.value = true;
      }
    },
    // 浏览器里打开企微后端
    openWecomConsolePage() {
      dispatchRouter('https://work.weixin.qq.com/wework_admin/frame#apps');
    },
    // 浏览器里打开操作手册
    openManual() {
      dispatchRouter(httpUrl.staticCDNURLMap.userManualUrl);
    },
  };

  watch(
    () => store.getters.enableWeworkLogin,
    (val: boolean) => {
      originData.enableWeworkLogin = store.getters.enableWeworkLogin;
      modifyData.enableWeworkLogin = val;
    },
  );
  watch(
    () => store.getters.enableYufuLogin,
    (val: boolean) => {
      originData.enableYufuLogin = store.getters.enableYufuLogin;
      modifyData.enableYufuLogin = val;
    },
  );
  watch(
    () => ssoWayOptionsValue.value,
    (val: boolean) => {
      originData.ssoWay = ssoWayOptionsValue.value;
      modifyData.ssoWay = val;
    },
  );

  watch(
    () => store.getters.enableOpenLdapLogin,
    (val: boolean) => {
      modifyData.enableOpenLdapLogin = val;
      originData.enableOpenLdapLogin = val;
    },
  );
  watch(
    () => store.getters.enableWindowsAdLogin,
    (val: boolean) => {
      modifyData.enableWindowsAdLogin = val;
      originData.enableWindowsAdLogin = val;
    },
  );

  watch(
    () => store.getters.syncWay,
    (val: string) => {
      originData.syncWay = val;
      modifyData.syncWay = val;
    },
  );

  return {
    showWeworkSelector,
    showWework,
    showWeworkDelegate,
    showWeworkDelegateConfigInfo,
    showOrganization,
    showSSO,
    loadingCode,
    qrCodeUrl,
    delegateOrgInfo,
    disableSave,
    disableOperate,
    noneAuthActive,
    anyLoginActive,
    anySyncActive,
    originData,
    modifyData,
    configStatusTips,
    methods,
  };
}
